import SupportedCards from '../../images/supported_cards.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'react-bootstrap/Button';
import { useToasts } from 'react-toast-notifications';
import DefaultLogo from '../../images/default-avatar.jpg';
import InputMask from 'react-input-mask';
import { useState, useEffect } from 'react';
import { s3Link } from '../../utils/constants';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Spinner from 'react-bootstrap/Spinner';
import PAlert from '../PAlert';
import currency from 'currency.js';
import { isLogin } from '../../utils/helper';
import { useDispatch } from 'react-redux';
import { setAlert } from '../../../redux/actions';

const valid = require("card-validator");

const PCreditCardForm = ({ user, setPaymentShow, setPaymentSuccessful }) => {
    // const { addToast } = useToasts();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertTitle, setAlertTitle] = useState(false);
    const [alertBody, setAlertBody] = useState('');
    const [fees, setFees] = useState({});
    const [promoUrl, setPromoUrl] = useState({});

    const formik = useFormik({
        initialValues: {
            name: '',
            credit_card: '',
            expiry: '',
            cvv: '',
            postal: ''
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .required('Name is required')
                .test(
                    'is-valid-card-name',
                    'Invalid credit card holder',
                    (value, context) => {
                        const cardValidation = valid.cardholderName(value);
                        return cardValidation.isValid;
                    },
                ),
            credit_card: Yup.string()
                .required('Credit card is required')
                .min(15, 'Must be at least 15 digits')
                .test(
                    'is-valid-credit-card',
                    'Invalid credit card',
                    (value, context) => {
                        const val = value ? value.replace(/_/g, '') : value;
                        const cardValidation = valid.number(val);
                        return cardValidation.isPotentiallyValid;
                    },
                ),
            expiry: Yup.string()
                .required('Expiry date is required')
                .test(
                    'is-valid-card-name',
                    'Invalid expiry date',
                    (value, context) => {
                        const cardValidation = valid.expirationDate(value);
                        return cardValidation.isValid;
                    },
                ),
            cvv: Yup.string()
                .required('CVV year is required')
                .test(
                    'is-valid-card-name',
                    'Invalid CVV',
                    (value, context) => {
                        const cardValidation = valid.cvv(value);
                        return cardValidation.isValid;
                    },
                ),
            postal: Yup.string()
                .required('Zip/Postal code is required')
                .test(
                    'is-valid-postal-code',
                    'Invalid postal/zip code',
                    (value, context) => {
                        const cardValidation = valid.postalCode(value);
                        return cardValidation.isValid;
                    },
                ),
        }),
        onSubmit: async (values, { setErrors }) => {
            setIsLoading(true);
            let payload = {
                ...values,
                user_id: user.id,
                type: 'subscription'
            };

            if (promoUrl && Object.keys(promoUrl).length > 0) {
                payload.link = promoUrl.link;
            }

            await axios.post(`/api/transactions`, payload)
                .then((response) => {
                    console.log({ response });

                    if (!response.data.success) {
                        window.location = response.data.url;
                        return;
                    }

                    if (promoUrl) {
                        localStorage.removeItem('promo_url');
                    }

                    setPaymentShow(false);
                    setPaymentSuccessful(true);

                    dispatch(setAlert({
                        show: true,
                        type: 'success',
                        body: 'Subscription successful'
                    }));

                    setIsLoading(false);


                })
                .catch((error) => {
                    console.error({ error });
                    if (error.response) {
                        if (error.response.status === 422) {
                            Object.keys(error.response.data.errors).forEach((key) => {
                                if (key == 'credit_card') {
                                    let errorMessage = error.response.data.errors['credit_card'][0];

                                    if (errorMessage.indexOf('insufficient funds') > 0) {
                                        errorMessage = `${errorMessage}. Click <a href="/payment-details">here</a> to change your card details.`;
                                    }
                                    dispatch(setAlert({
                                        show: true,
                                        type: 'error',
                                        body: errorMessage,
                                        title: 'Bank Transaction Failed'
                                    }));
                                }
                                setErrors({
                                    [key]: error.response.data.errors[key][0],
                                });
                            });
                        }

                        if (error.response.status === 402) {
                            dispatch(setAlert({
                                show: true,
                                type: 'error',
                                body: error.response.data.reason,
                                title: 'Bank Transaction Failed'
                            }));

                            formik.setFieldValue('expiry', `${error.response.data.card.exp_month}/${error.response.data.card.exp_year}`);
                            setErrors({
                                credit_card: error.response.data.reason
                            });
                        }

                        if (error.response.status === 404) {
                            localStorage.removeItem('promo_url');
                            dispatch(setAlert({
                                show: true,
                                body: error.response.data.message || 'Discount link has already expired',
                                type: 'error',
                                onClose: () => {
                                    window.location.reload();
                                }
                            }));

                            return;
                        }
                    } else {
                        dispatch(setAlert({
                            show: true,
                            type: 'error',
                            body: 'Subscription failed. Please contact your bank.',
                            title: 'Subscription failed'
                        }));
                    }
                    setIsLoading(false);
                });
        },
    });

    const checkFees = async () => {
        await axios.get(`/api/transactions/fees`, {
            params: {
                pu: user.id
            }
        })
            .then(response => {
                console.log(response.data);
                setFees(response.data);
            });
    };

    useEffect(() => {
        if (isLogin()) {
            checkFees();
        }

        // Promo Code URL
        const promoUrlDetails = JSON.parse(localStorage.getItem('promo_url'));
        if (promoUrlDetails && promoUrlDetails.user.id === user.id) {
            // promo code here
            setPromoUrl(promoUrlDetails);
        }
    }, []);

    const handleCardNumberChange = e => {
        const value = e.target.value.replace(/\D/g, '');
        formik.setFieldValue('credit_card', value, true);
    };

    const RenderPrice = () => {
        if (promoUrl && Object.keys(promoUrl).length > 0) {
            return <span>{currency(promoUrl.link_price).format({ precision: 2 })}*</span>;
        }

        if (user.creator.promo_details && Object.keys(user.creator.promo_details).length > 0) {
            return <span>{currency(user.creator.promo_details.promo_price).format({ precision: 2 })}*</span>;
        } else {
            return <span>{currency(user.creator.subscription_price).format({ precision: 2 })}*</span>;
        }
    };

    return (
        <>
            <div className="modalpayment-complete-text">{user.creator.promo_details && Object.keys(user.creator.promo_details).length > 0 ? 'Complete your first month promotion payment ' : 'Complete your monthly payment to '}</div>
            <div className="user-nme-price">
                @
                {user.login}
                {' '}
                <span>for </span> <RenderPrice />
                {' '}
                <span> USD</span>
            </div>
            <div className="card-img-usr">
                {
                    user.profile_pic
                        ? <LazyLoadImage src={`${s3Link}/${user.id}/profile_photo.jpg`} />
                        : <svg style={{ backgroundColor: "#ffffff" }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="#0097fe" d="M399 384.2C376.9 345.8 335.4 320 288 320H224c-47.4 0-88.9 25.8-111 64.2c35.2 39.2 86.2 63.8 143 63.8s107.8-24.7 143-63.8zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm256 16a72 72 0 1 0 0-144 72 72 0 1 0 0 144z" /></svg>
                }
            </div>
            <div className="panel panel-default">
                <div className="panel-heading">
                    <div className="cards-logo">
                        <LazyLoadImage src={SupportedCards} height={40} />
                    </div>
                </div>
                <form onSubmit={formik.handleSubmit}>
                    <div className="panel-body">
                        <div className="card-no">
                            <input
                                type="text"
                                id="name"
                                name="name"
                                placeholder="Card Holder Name**"
                                className={`form-control${formik.errors.name && formik.touched.name ? ' is-invalid' : ''}`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                            />
                            {formik.errors.name && formik.touched.name ? <div className="invalid-feedback">{formik.errors.name}</div> : null}
                        </div>
                        <div className="card-no">

                            <input
                                type='text'
                                id="credit_card"
                                name="credit_card"
                                placeholder="Credit Card Number"
                                className={`ccFormatMonitor cc-number${formik.errors.credit_card && formik.touched.credit_card ? ' is-invalid' : ''}`}
                                onChange={handleCardNumberChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.credit_card}
                                inputMode="numeric"
                                maxLength="16"
                            />
                            <FontAwesomeIcon icon={['fas', 'lock']} />
                            {formik.errors.credit_card && formik.touched.credit_card ? <div className="invalid-feedback" dangerouslySetInnerHTML={{ __html: formik.errors.credit_card }} /> : null}
                        </div>
                        <div className="card-detail">
                            {/* <input type="tel" id="expire" className="expiryFormatMonitor cc-exp" name="expire" placeholder="Expiry" /> */}
                            <InputMask
                                mask="99/99"
                                id="expiry"
                                name="expiry"
                                placeholder="MM/YY"
                                className={`form-control${formik.errors.expiry && formik.touched.expiry ? ' is-invalid' : ''}`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.expiry}
                                inputMode="numeric"
                            />
                            <InputMask
                                mask="999"
                                id="cvv"
                                name="cvv"
                                placeholder="CVV"
                                className={`cc-cvc form-control${formik.errors.cvv && formik.touched.cvv ? ' is-invalid' : ''}`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.cvv}
                                inputMode="numeric"
                            />
                            <input
                                type="text"
                                id="postal"
                                name="postal"
                                placeholder="Zip/Postal"
                                className={`form-control${formik.errors.postal && formik.touched.postal ? ' is-invalid' : ''}`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.postal}
                            />
                        </div>
                        {formik.errors.expiry && formik.touched.expiry ? <div className="invalid-feedback">{formik.errors.expiry}</div> : null}
                        {formik.errors.cvv && formik.touched.cvv ? <div className="invalid-feedback">{formik.errors.cvv}</div> : null}
                        {formik.errors.postal && formik.touched.postal ? <div className="invalid-feedback">{formik.errors.postal}</div> : null}
                        <div className="follow-btn">
                            <Button
                                type="submit"
                                disabled={isLoading}
                                className="btn btn-theme-sm blue-btn-hover"
                                id="payBtn"
                            >
                                {isLoading && <Spinner animation="border" variant="light" size="sm" />}
                                {' '}
                                {isLoading ? 'Processing...' : 'Follow'}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
            <div className="secure-payment">
                <FontAwesomeIcon icon={['fas', 'lock']} /> Secure Checkout
            </div>
            <div id="getcalculation_login">
                <p>By clicking "Follow" you confirm payment of <RenderPrice /> plus processing fee for the first month, followed by ${user.creator.subscription_price} monthly plus processing fee until you cancel your subscription.</p>
                {
                    fees.fees && !!fees.reg_gst && fees.gst > 0
                        ? <p>*{currency(fees.fees).format({ precision: 2 })} processing fee plus {currency(fees.gst).format({ precision: 2 })} GST will apply.</p>
                        : <p>*{currency(fees.fees).format({ precision: 2 })} processing fee will apply.</p>

                }
            </div>
            <p>
                By making this payment, you agree to {' '}<a href="/terms/" target="_blank">Privaposts Terms of Use</a>.
            </p>
            <p>**Enter your card holder name exactly as it is on your card. This is completely confidential and does not appear on statements and is used for credit card verification by our secure bank gateway provider.</p>

            <PAlert show={showAlert} setShow={setShowAlert} title={alertTitle} body={alertBody} />
        </>
    );
};

export default PCreditCardForm;
